const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
export const MAPS_ENABLED = !!MAPS_KEY;
export const MAPS_AUTH_EVENT = "gmaps-auth-failure";

const TAG = "[GoogleMaps]";
let mapsPromise = null;
let diagnosed = false;

const placesReady = () => !!window.google?.maps?.places?.Autocomplete;

// Google calls this global when the key is invalid, referrer blocked, API not enabled or billing inactive.
window.gm_authFailure = () => {
  console.error(`${TAG} Authentication failed. Check: key valid, HTTP referrer allowed for this domain, "Maps JavaScript API" + "Places API" enabled, billing active on the Google Cloud project.`);
  window.dispatchEvent(new Event(MAPS_AUTH_EVENT));
};

// One-time browser-side check that the key + Places library actually return predictions.
function diagnosePlaces() {
  if (diagnosed || !window.google?.maps?.places?.AutocompleteService) return;
  diagnosed = true;
  try {
    new window.google.maps.places.AutocompleteService().getPlacePredictions(
      { input: "Mumbai", componentRestrictions: { country: "in" } },
      (res, status) => {
        if (status === "OK") console.info(`${TAG} Places check OK — key valid, Places library loaded (${res?.length || 0} predictions).`);
        else if (status === "REQUEST_DENIED") console.error(`${TAG} Places check REQUEST_DENIED — Places API not enabled for this key, key restrictions block it, or billing is inactive.`);
        else console.warn(`${TAG} Places check status: ${status}`);
      }
    );
  } catch (e) {
    console.error(`${TAG} Places check threw:`, e);
  }
}

export function loadGoogleMaps() {
  if (!MAPS_KEY) {
    console.error(`${TAG} REACT_APP_GOOGLE_MAPS_API_KEY is not set — address search disabled.`);
    return Promise.reject(new Error("Google Maps API key not configured"));
  }
  if (placesReady()) { diagnosePlaces(); return Promise.resolve(true); }
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    const fail = (msg) => {
      mapsPromise = null;
      console.error(`${TAG} ${msg}`);
      reject(new Error(msg));
    };
    const ready = async () => {
      try {
        if (window.google?.maps?.importLibrary) await window.google.maps.importLibrary("places");
        if (!placesReady()) return fail("Script loaded but Places library is unavailable.");
        diagnosePlaces();
        resolve(true);
      } catch (e) {
        fail(`Places library failed to initialise: ${e?.message || e}`);
      }
    };
    window.__arrowMapsReady = ready;

    const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existing) {
      if (window.google?.maps?.importLibrary) ready();
      else existing.addEventListener("load", () => setTimeout(ready, 0));
      return;
    }
    const s = document.createElement("script");
    s.id = "google-maps-js";
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places&loading=async&callback=__arrowMapsReady`;
    s.async = true;
    s.onerror = () => fail("Script failed to load (network/ad-blocker/CSP).");
    document.head.appendChild(s);
  });
  return mapsPromise;
}

// Attach a Places Autocomplete to an input. Returns the instance (or null).
export function attachAutocomplete(el, onPlace, opts = {}) {
  if (!el || !placesReady()) return null;
  const ac = new window.google.maps.places.Autocomplete(el, {
    fields: ["address_components", "formatted_address", "geometry", "name"],
    componentRestrictions: { country: opts.country || "in" },
    ...(opts.types ? { types: opts.types } : {}),
  });
  ac.addListener("place_changed", () => {
    const place = ac.getPlace();
    if (place) onPlace(place);
  });
  return ac;
}

export function detachAutocomplete(ac) {
  if (ac && window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(ac);
}

// Parse a Google Places result into our normalized address shape.
export function parsePlace(place) {
  const comps = place?.address_components || [];
  const get = (type, short = false) => {
    const c = comps.find((x) => x.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : "";
  };
  const loc = place?.geometry?.location;
  return {
    formatted_address: place?.formatted_address || place?.name || "",
    city: get("locality") || get("postal_town") || get("administrative_area_level_2") || get("administrative_area_level_3") || "",
    state: get("administrative_area_level_1") || "",
    country: get("country") || "",
    pincode: get("postal_code") || "",
    lat: loc ? loc.lat() : null,
    lng: loc ? loc.lng() : null,
  };
}
